import { useEffect, useState } from 'react';
import { callApi } from './api';
import { INTAKE_SECTIONS } from './hocchokenIntakeFields';
import { buildHocchokenCaseWordBlob } from './hocchokenExport';
import { downloadBlob } from './wordExport';

const STAFF_OTHER = '__other__';

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function IntakeField({ field, value, onChange }) {
  if (field.type === 'select') {
    return (
      <select value={value} onChange={(e) => onChange(field.key, e.target.value)}>
        <option value="">未選択</option>
        {field.options.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </select>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea
        className="editor-textarea hc-intake-textarea"
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
    );
  }
  return (
    <input
      type={field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : 'text'}
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => onChange(field.key, e.target.value)}
    />
  );
}

/** 対応職員の選択（hc_staffの4名＋その他）。「その他」を選ぶと自由入力欄が出る。 */
function StaffPicker({ staff, value, isOther, onChange, disabled }) {
  const selectValue = isOther ? STAFF_OTHER : ((staff || []).some((s) => s.name === value) ? value : '');
  return (
    <>
      <select
        value={selectValue}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === STAFF_OTHER) onChange('', true);
          else onChange(v, false);
        }}
      >
        <option value="" disabled hidden>対応職員を選択</option>
        {(staff || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
        <option value={STAFF_OTHER}>その他</option>
      </select>
      {isOther && (
        <input
          type="text" placeholder="職員名を入力" value={value}
          onChange={(e) => onChange(e.target.value, true)}
          disabled={disabled}
        />
      )}
    </>
  );
}

/** ほおっちょけん：相談案件を作り、経過を都度書き足していく画面。 */
export default function HocchokenCasesScreen({ onBack }) {
  const [staff, setStaff] = useState(null); // 対応職員の選択肢（100歳体操の当番と共通のhc_staff）

  const [cases, setCases] = useState(null); // null=読み込み中
  const [listError, setListError] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [detail, setDetail] = useState(null); // {case, entries}
  const [detailError, setDetailError] = useState('');

  const [intakeDraft, setIntakeDraft] = useState({});
  const [mitigationDraft, setMitigationDraft] = useState('');
  const [resultDraft, setResultDraft] = useState('');
  const [savingIntake, setSavingIntake] = useState(false);
  const [intakeSaved, setIntakeSaved] = useState(false);
  const [intakeError, setIntakeError] = useState('');

  const [entryDate, setEntryDate] = useState(todayStr());
  const [entryStaffName, setEntryStaffName] = useState('');
  const [entryStaffOther, setEntryStaffOther] = useState(false);
  const [entryContent, setEntryContent] = useState('');
  const [addingEntry, setAddingEntry] = useState(false);

  const [editingEntryId, setEditingEntryId] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editStaffName, setEditStaffName] = useState('');
  const [editStaffOther, setEditStaffOther] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [savingEntryEdit, setSavingEntryEdit] = useState(false);
  const [entryEditError, setEntryEditError] = useState('');

  const [closing, setClosing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    callApi('hocchoken_staff_list').then((data) => setStaff(data.staff)).catch(() => {});
  }, []);

  function loadCases() {
    return callApi('hocchoken_case_list')
      .then((data) => setCases(data.cases))
      .catch((err) => setListError(err.message));
  }

  useEffect(() => { loadCases(); }, []);

  function loadDetail(caseId) {
    setDetail(null);
    setDetailError('');
    callApi('hocchoken_case_get', { caseId })
      .then((data) => {
        setDetail(data);
        setIntakeDraft(data.case.intake || {});
        setMitigationDraft(data.case.mitigation || '');
        setResultDraft(data.case.result || '');
      })
      .catch((err) => setDetailError(err.message));
  }

  function openCase(caseId) {
    setSelectedCaseId(caseId);
    setEntryDate(todayStr());
    setEntryStaffName('');
    setEntryStaffOther(false);
    setEntryContent('');
    setEditingEntryId('');
    setDownloadError('');
    setIntakeSaved(false);
    loadDetail(caseId);
  }

  async function handleCreateCase(e) {
    e.preventDefault();
    if (!newTitle.trim() || creating) return;
    setCreating(true);
    setListError('');
    try {
      const data = await callApi('hocchoken_case_create', { title: newTitle.trim() });
      setNewTitle('');
      await loadCases();
      openCase(data.case.id);
    } catch (err) {
      setListError(err.message);
    } finally {
      setCreating(false);
    }
  }

  function updateIntakeField(key, value) {
    setIntakeDraft((prev) => ({ ...prev, [key]: value }));
    setIntakeSaved(false);
  }

  async function handleSaveIntake() {
    if (savingIntake) return;
    setSavingIntake(true);
    setIntakeError('');
    try {
      await callApi('hocchoken_case_update', {
        caseId: selectedCaseId,
        intake: intakeDraft,
        mitigation: mitigationDraft,
        result: resultDraft,
      });
      setIntakeSaved(true);
      loadDetail(selectedCaseId);
      loadCases();
    } catch (err) {
      setIntakeError(err.message);
    } finally {
      setSavingIntake(false);
    }
  }

  async function handleAddEntry(e) {
    e.preventDefault();
    if (!entryContent.trim() || !entryDate || addingEntry) return;
    setAddingEntry(true);
    setDetailError('');
    try {
      await callApi('hocchoken_case_entry_add', {
        caseId: selectedCaseId, entryDate, staffName: entryStaffName, content: entryContent.trim(),
      });
      setEntryContent('');
      setEntryStaffName('');
      setEntryStaffOther(false);
      loadDetail(selectedCaseId);
      loadCases();
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setAddingEntry(false);
    }
  }

  function startEditEntry(entry) {
    setEditingEntryId(entry.id);
    setEditDate(entry.entry_date);
    setEditContent(entry.content);
    const isKnown = (staff || []).some((s) => s.name === entry.staff_name);
    setEditStaffOther(!isKnown && Boolean(entry.staff_name));
    setEditStaffName(entry.staff_name || '');
    setEntryEditError('');
  }

  function cancelEditEntry() {
    setEditingEntryId('');
  }

  async function handleSaveEntryEdit() {
    if (savingEntryEdit || !editContent.trim() || !editDate) return;
    setSavingEntryEdit(true);
    setEntryEditError('');
    try {
      await callApi('hocchoken_case_entry_update', {
        entryId: editingEntryId, entryDate: editDate, staffName: editStaffName, content: editContent.trim(),
      });
      setEditingEntryId('');
      loadDetail(selectedCaseId);
    } catch (err) {
      setEntryEditError(err.message);
    } finally {
      setSavingEntryEdit(false);
    }
  }

  async function handleToggleClose() {
    if (closing || !detail) return;
    setClosing(true);
    setDetailError('');
    try {
      const reopen = detail.case.status === 'closed';
      await callApi('hocchoken_case_close', { caseId: selectedCaseId, reopen });
      loadDetail(selectedCaseId);
      loadCases();
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setClosing(false);
    }
  }

  async function handleDownload() {
    if (downloading || !detail) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const blob = await buildHocchokenCaseWordBlob(detail.case, detail.entries);
      downloadBlob(blob, `${detail.case.title}_相談記録.docx`);
    } catch (err) {
      setDownloadError('Wordファイルの作成に失敗しました。');
    } finally {
      setDownloading(false);
    }
  }

  const selectedCase = detail?.case;

  return (
    <div className="workspace is-wide">
      <div className="workspace-head reveal r1">
        <button className="back-link" type="button" onClick={selectedCaseId ? () => setSelectedCaseId('') : onBack}>
          ← {selectedCaseId ? '案件一覧へ戻る' : 'ホームへ戻る'}
        </button>
        <h1 className="workspace-title">ほおっちょけん 相談記録</h1>
      </div>

      {!selectedCaseId && (
        <>
          <form className="editor-card reveal r2" onSubmit={handleCreateCase}>
            <h2 className="admin-dept-title">新しい案件を作る</h2>
            <p className="admin-lock-note">案件名だけ決めれば作成できます。受付票の詳細は次の画面で入力・保存できます。</p>
            <div className="admin-lock-row">
              <input
                type="text" className="admin-password-input" placeholder="案件名（例：〇〇様・歩道の陥没について）"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={creating}
              />
              <button className="enter admin-unlock-btn" type="submit" disabled={!newTitle.trim() || creating}>
                {creating ? '作成中…' : '作成する'}
              </button>
            </div>
          </form>

          {cases === null && !listError && <p className="workspace-loading reveal r3">案件を読み込んでいます…</p>}
          {listError && <p className="pin-error reveal r3">{listError}</p>}

          {cases && cases.length === 0 && (
            <p className="workspace-loading reveal r3">まだ案件がありません。上のフォームから作成してください。</p>
          )}

          {cases && cases.length > 0 && (
            <div className="editor-card reveal r3">
              {cases.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="hc-case-row"
                  onClick={() => openCase(c.id)}
                >
                  <span className="hc-case-title">{c.title}</span>
                  <span className={`hc-case-status${c.status === 'closed' ? ' is-closed' : ''}`}>
                    {c.status === 'closed' ? '対応終了' : '対応中'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selectedCaseId && (
        <>
          {!detail && !detailError && <p className="workspace-loading reveal r2">読み込んでいます…</p>}
          {detailError && <p className="pin-error reveal r2">{detailError}</p>}

          {selectedCase && (
            <>
              <div className="editor-card reveal r2">
                <div className="hc-case-detail-head">
                  <h2 className="admin-dept-title">{selectedCase.title}</h2>
                  <div className="result-actions">
                    <button className="copy-all-btn" type="button" disabled={closing} onClick={handleToggleClose}>
                      {selectedCase.status === 'closed' ? '対応中に戻す' : '対応終了にする'}
                    </button>
                    <button className="enter word-download-btn" type="button" disabled={downloading} onClick={handleDownload}>
                      {downloading ? '作成中…' : 'Wordでダウンロード'}
                    </button>
                  </div>
                </div>
                {downloadError && <p className="pin-error" role="alert">{downloadError}</p>}
              </div>

              <div className="editor-card reveal r2">
                <h2 className="admin-dept-title">対応経過</h2>
                <div className="hc-entry-list">
                  {detail.entries.length === 0 && (
                    <p className="workspace-loading">まだ経過記録がありません。下から追加してください。</p>
                  )}
                  {detail.entries.map((entry) => (
                    <div className="hc-entry-row" key={entry.id}>
                      {editingEntryId === entry.id ? (
                        <div className="hc-entry-edit">
                          <div className="admin-lock-row">
                            <input
                              type="date" value={editDate}
                              onChange={(e) => setEditDate(e.target.value)}
                              disabled={savingEntryEdit}
                            />
                            <StaffPicker
                              staff={staff} value={editStaffName} isOther={editStaffOther}
                              onChange={(name, isOther) => { setEditStaffName(name); setEditStaffOther(isOther); }}
                              disabled={savingEntryEdit}
                            />
                          </div>
                          <textarea
                            className="editor-textarea"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            disabled={savingEntryEdit}
                          />
                          <div className="admin-save-row">
                            <button
                              className="enter admin-save-btn" type="button"
                              disabled={savingEntryEdit || !editContent.trim()}
                              onClick={handleSaveEntryEdit}
                            >
                              {savingEntryEdit ? '保存中…' : '保存'}
                            </button>
                            <button
                              type="button" className="pii-replace-btn"
                              disabled={savingEntryEdit}
                              onClick={cancelEditEntry}
                            >
                              キャンセル
                            </button>
                          </div>
                          {entryEditError && <p className="pin-error" role="alert">{entryEditError}</p>}
                        </div>
                      ) : (
                        <>
                          <div className="hc-entry-row-head">
                            <span className="hc-entry-date">
                              {entry.entry_date}{entry.staff_name ? `　対応：${entry.staff_name}` : ''}
                            </span>
                            <button type="button" className="copy-item-btn" onClick={() => startEditEntry(entry)}>
                              編集
                            </button>
                          </div>
                          <p className="hc-entry-content">{entry.content}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <form className="hc-entry-form" onSubmit={handleAddEntry}>
                  <h3 className="conf-new-item-title">経過を追加</h3>
                  <div className="admin-lock-row">
                    <input
                      type="date"
                      value={entryDate}
                      onChange={(e) => setEntryDate(e.target.value)}
                      disabled={addingEntry}
                    />
                    <StaffPicker
                      staff={staff} value={entryStaffName} isOther={entryStaffOther}
                      onChange={(name, isOther) => { setEntryStaffName(name); setEntryStaffOther(isOther); }}
                      disabled={addingEntry}
                    />
                  </div>
                  <textarea
                    className="editor-textarea"
                    placeholder="今回の経過を入力してください"
                    value={entryContent}
                    onChange={(e) => setEntryContent(e.target.value)}
                    disabled={addingEntry}
                  />
                  <div className="admin-save-row">
                    <button className="enter admin-save-btn" type="submit" disabled={!entryContent.trim() || addingEntry}>
                      {addingEntry ? '追加中…' : 'この経過を追加'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="editor-card reveal r3">
                <h2 className="admin-dept-title">受付票（様式第１号－①）</h2>

                {INTAKE_SECTIONS.map((section) => (
                  <div className="hc-intake-section" key={section.title}>
                    <h3 className="conf-new-item-title">{section.title}</h3>
                    <div className="word-form-grid">
                      {section.fields.map((field) => (
                        <label className={`word-form-field${field.type === 'textarea' ? ' hc-intake-field-wide' : ''}`} key={field.key}>
                          <span>{field.label}</span>
                          <IntakeField field={field} value={intakeDraft[field.key] ?? ''} onChange={updateIntakeField} />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="hc-intake-section">
                  <h3 className="conf-new-item-title">軽減・解決策</h3>
                  <textarea
                    className="editor-textarea"
                    value={mitigationDraft}
                    onChange={(e) => { setMitigationDraft(e.target.value); setIntakeSaved(false); }}
                  />
                </div>
                <div className="hc-intake-section">
                  <h3 className="conf-new-item-title">結果</h3>
                  <textarea
                    className="editor-textarea"
                    value={resultDraft}
                    onChange={(e) => { setResultDraft(e.target.value); setIntakeSaved(false); }}
                  />
                </div>

                <div className="admin-save-row">
                  <button className="enter admin-save-btn" type="button" disabled={savingIntake} onClick={handleSaveIntake}>
                    {savingIntake ? '保存中…' : '受付票の内容を保存'}
                  </button>
                  {intakeSaved && <span className="admin-saved-label">保存しました</span>}
                </div>
                {intakeError && <p className="pin-error" role="alert">{intakeError}</p>}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
